import React from 'react';
import Arg from '@spothero/arg';
import CityAPI from 'api/city';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import AirportAPI from 'api/airport';
import {loadable} from 'router/routes';
import {createCleanedData, fetchCleanedData} from 'router/router-utils';
import {Page} from './utils';
import CityLegacy from './CityLegacy';
import DestinationAPI from 'api/destination';
import Destination from './Destination';
import SearchAirportPage from './SearchAirport';

const SearchMobileTransient = loadable(() =>
    import('search/SearchMobileTransient')
);
const SearchMobileMonthly = loadable(() =>
    import('search/SearchMobileMonthly')
);
const SearchMobileEvent = loadable(() => import('search/SearchMobileEvent'));
const SearchMobileDestination = loadable(() =>
    import('search/SearchMobileDestination')
);
const SearchDesktopTransient = loadable(() =>
    import('search/SearchDesktopTransient')
);
const SearchDesktopMonthly = loadable(() =>
    import('search/SearchDesktopMonthly')
);
const SearchDesktopEvent = loadable(() => import('search/SearchDesktopEvent'));
const SearchDesktopDestination = loadable(() =>
    import('search/SearchDesktopDestination')
);
const SearchAirport = loadable(() => import('search/airport/SearchAirport'));

const Search = ({configData, isMonthly, destination, pageType}) => {
    const {isMobile} = configData;

    if (isMonthly) {
        return isMobile ? <SearchMobileMonthly /> : <SearchDesktopMonthly />;
    }

    if (destination?.airport) {
        return <SearchAirport />;
    }

    if (isMobile) {
        return pageType === 'event' ? (
            <SearchMobileEvent />
        ) : !isEmpty(destination) ? (
            <SearchMobileDestination />
        ) : (
            <SearchMobileTransient />
        );
    }

    return pageType === 'event' ? (
        <SearchDesktopEvent />
    ) : !isEmpty(destination) ? (
        <SearchDesktopDestination />
    ) : (
        <SearchDesktopTransient />
    );
};

async function getCitySlug(cityId, searchParams) {
    if (!isEmpty(searchParams)) {
        const {
            page_info: {
                setup: {
                    city: {slug},
                },
            },
        } = searchParams;

        return slug;
    }

    const {slug} = await CityAPI.getById(cityId);

    return slug;
}

const AirportSearchInitialState = ({state, citySlug, destination, query}) => {
    const {
        relative_url: slug,
        airport: {iata_code: iataCode},
    } = destination;

    return SearchAirportPage.getInitialState({
        state: {
            ...state,
            destination: {data: destination},
        },
        params: {city: citySlug, slug, iata: iataCode},
        query,
    });
};

async function searchCity({id, state, searchParams, query, cookies}) {
    const city = await getCitySlug(id, searchParams);

    return CityLegacy.getInitialState({
        state,
        params: {city},
        query,
        cookies,
    });
}

async function searchDestination({id, state, searchParams, query, cookies}) {
    const destination = await DestinationAPI.get(id).then(res => res.data.data);
    const {relative_url: slug, spothero_city: cityId, airport} = destination;
    const citySlug = await getCitySlug(cityId, searchParams);

    if (airport) {
        return AirportSearchInitialState({
            state,
            citySlug,
            destination,
            query,
        });
    } else {
        return Destination.getInitialState({
            state: {
                ...state,
                destination: {data: destination},
            },
            params: {city: citySlug, slug},
            query,
            cookies,
        });
    }
}

async function searchAirport({id, state, searchParams, query, cookies}) {
    const {
        data: {data: destination},
    } = await AirportAPI.getByCode(id);
    const {spothero_city: cityId} = destination;
    const citySlug = await getCitySlug(cityId, searchParams);

    return AirportSearchInitialState({
        state,
        citySlug,
        destination,
        query,
    });
}

Search.getInitialState = ({state, query, route, cookies}) => {
    const {
        location: {state: searchParams = {}},
    } = route;
    const {id, kind} = query;
    const methodParams = {
        state,
        searchParams,
        query,
        cookies,
    };

    if (!isNil(kind)) {
        // new /search experience with kind and id provided in query string to determine how search should behave
        if (kind === 'address' && !isEmpty(searchParams)) {
            // address searches from the UI do not have an ID so we use the cleaned params returned by the search-params
            // endpoint, which has already happened in the search controls, to execute the proper search and
            // `searchParams` is passed through the location 'state' in order to route using search metadata
            // that means there is no need to call the search-params endpoint since they're already available
            // and happens after initial page load when the user performs a search through the UI
            const {city, search: searchState} = state;

            // This overrides incorrect starts/ends coming back from the search-params response
            // This is only necessary due to way Search.getInitialState handles address search pages
            // TODO: Remove once BE issue is resolved https://spothero.atlassian.net/browse/CONWEB-291
            searchParams.starts = query.starts || searchParams.starts;
            searchParams.ends = query.ends || searchParams.ends;

            return createCleanedData({
                currentCity: city,
                cleanedParams: searchParams,
                currentSearchState: searchState,
            });

            // if searchParams is empty, that means this search was initialized on page load and not through the UI
            // just allow it to fall through to the bottom of this method where we will fetch new cleaned data based on
            // the url that the user has provided in the address bar
        } else if (!isNil(id)) {
            methodParams.id = id;

            switch (kind) {
                case Page.CITY:
                    return searchCity(methodParams);

                case Page.DESTINATION:
                    return searchDestination(methodParams);

                case Page.AIRPORT:
                    return searchAirport(methodParams);

                case Page.EVENT:
                    query.eid = id;

                    // translate /search?kind=event&id=1 URL to /search?eid=1 for calling fetchCleanedData since it only responds with
                    // that format for event kind searches, otherwise it would error without altering the BE to accept this type of URL
                    return fetchCleanedData({
                        state,
                        route: {
                            location: {
                                pathname: '/search',
                                search: `?${Arg.stringify(query)}`,
                            },
                        },
                    });
            }
        }
    } else {
        // TODO: Remove support for these URLs when URL restructuring project is completed, they were "v2" URLs but not
        // flexible enough for all use cases. The new URLs with "kind" and "id" parameters will be used moving forward
        if (!isNil(query['city-id'])) {
            return searchCity({
                id: query['city-id'],
                ...methodParams,
            });
        } else if (!isNil(query['destination-id'])) {
            return searchDestination({
                id: query['destination-id'],
                ...methodParams,
            });
        } else if (!isNil(query['airport-code'])) {
            return searchAirport({
                id: query['airport-code'],
                ...methodParams,
            });
        }
    }

    // regular address search on initial page load, run a new search based on the url
    return fetchCleanedData({state, route});
};

Search.propTypes = {
    configData: PropTypes.object,
    isMonthly: PropTypes.bool,
    destination: PropTypes.object,
    pageType: PropTypes.string,
};

export default Search;
